import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import { getDecorations } from './decoration';
import { applyReplace, applyReplaceAll, getRegex } from './replace';
import { processSearches, type SearchResult } from './search';

export interface ZySearchAndReplaceStorage {
    searchTerm: string;
    lastSearchTerm: string;
    replaceTerm: string;
    results: SearchResult[];
    hasReplaceableMatch: boolean;
    caseSensitive: boolean;
    lastCaseSensitive: boolean;
    wholeWord: boolean;
    lastWholeWord: boolean;
    resultIndex: number;
    regex: boolean;
    lastRegex: boolean;
}

type SearchPluginMeta = { type: 'updateResultIndex' };

declare module '@tiptap/core' {
    interface Storage {
        zySearchAndReplace: ZySearchAndReplaceStorage;
    }

    interface Commands<ReturnType> {
        search: {
            setSearchTerm: (searchTerm: string) => ReturnType;
            setReplaceTerm: (replaceTerm: string) => ReturnType;
            setCaseSensitive: (caseSensitive: boolean) => ReturnType;
            setWholeWord: (wholeWord: boolean) => ReturnType;
            setRegex: (regex: boolean) => ReturnType;
            resetIndex: () => ReturnType;
            nextSearchResult: () => ReturnType;
            previousSearchResult: () => ReturnType;
            replace: () => ReturnType;
            replaceAll: () => ReturnType;
        };
    }
}

function scrollActiveMatchIntoView(editor: Editor, results: SearchResult[], index: number) {
    try {
        const { from, nodeType } = results[index];
        let element: HTMLElement | null = null;

        if (nodeType === 'text') {
            const { node } = editor.view.domAtPos(from);
            element = node instanceof HTMLElement ? node : node.parentElement;
        } else {
            const dom = editor.view.nodeDOM(from);
            const nodeElement = dom instanceof HTMLElement ? dom : (dom?.parentElement ?? null);
            const matchedAttr = results[index].matchedAttr;
            if (nodeElement && matchedAttr) {
                element = nodeElement.querySelector<HTMLElement>(`[data-search-attr="${matchedAttr}"]`);
            }
            element ??= nodeElement;
        }

        element?.scrollIntoView({ block: 'nearest' });
    } catch {
        // Position couldn't be resolved (e.g. transient state); skip scrolling.
    }
}

function dispatchSearchPluginUpdate(editor: Editor, meta?: SearchPluginMeta) {
    const tr = editor.state.tr;
    if (meta) {
        tr.setMeta(zySearchAndReplacePluginKey, meta);
    }
    editor.view.dispatch(tr);
}

function decorationsForResultIndex(doc: Parameters<typeof DecorationSet.create>[0], results: SearchResult[], resultIndex: number) {
    if (results.length === 0) {
        return DecorationSet.empty;
    }

    return DecorationSet.create(doc, getDecorations(results, resultIndex));
}

export const zySearchAndReplacePluginKey = new PluginKey('zySearchAndReplacePlugin');

export const ZySearchAndReplace = Extension.create<ZySearchAndReplaceStorage>({
    name: 'zySearchAndReplace',

    addStorage() {
        return {
            searchTerm: '',
            replaceTerm: '',
            results: [],
            hasReplaceableMatch: false,
            lastSearchTerm: '',
            caseSensitive: false,
            lastCaseSensitive: false,
            wholeWord: false,
            lastWholeWord: false,
            resultIndex: 0,
            regex: false,
            lastRegex: false,
        };
    },

    addCommands() {
        return {
            setSearchTerm:
                (searchTerm: string) =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.searchTerm = searchTerm;
                    dispatchSearchPluginUpdate(editor);
                    return false;
                },
            setReplaceTerm:
                (replaceTerm: string) =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.replaceTerm = replaceTerm;
                    return false;
                },
            setCaseSensitive:
                (caseSensitive: boolean) =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.caseSensitive = caseSensitive;
                    dispatchSearchPluginUpdate(editor);
                    return false;
                },
            setWholeWord:
                (wholeWord: boolean) =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.wholeWord = wholeWord;
                    dispatchSearchPluginUpdate(editor);
                    return false;
                },
            setRegex:
                (regex: boolean) =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.regex = regex;
                    dispatchSearchPluginUpdate(editor);
                    return false;
                },
            resetIndex:
                () =>
                ({ editor }) => {
                    editor.storage.zySearchAndReplace.resultIndex = 0;
                    dispatchSearchPluginUpdate(editor, { type: 'updateResultIndex' });
                    return false;
                },
            nextSearchResult:
                () =>
                ({ editor }) => {
                    const { results, resultIndex } = editor.storage.zySearchAndReplace;

                    const nextIndex = resultIndex + 1;

                    if (results[nextIndex]) {
                        editor.storage.zySearchAndReplace.resultIndex = nextIndex;
                        scrollActiveMatchIntoView(editor, results, nextIndex);
                    } else {
                        editor.storage.zySearchAndReplace.resultIndex = 0;
                        scrollActiveMatchIntoView(editor, results, 0);
                    }

                    dispatchSearchPluginUpdate(editor, { type: 'updateResultIndex' });
                    return false;
                },
            previousSearchResult:
                () =>
                ({ editor }) => {
                    const { results, resultIndex } = editor.storage.zySearchAndReplace;

                    const prevIndex = resultIndex - 1;

                    if (results[prevIndex]) {
                        editor.storage.zySearchAndReplace.resultIndex = prevIndex;
                        scrollActiveMatchIntoView(editor, results, prevIndex);
                    } else {
                        editor.storage.zySearchAndReplace.resultIndex = results.length - 1;
                        scrollActiveMatchIntoView(editor, results, results.length - 1);
                    }

                    dispatchSearchPluginUpdate(editor, { type: 'updateResultIndex' });
                    return false;
                },
            replace:
                () =>
                ({ editor, tr }) => {
                    const { searchTerm, replaceTerm, results, resultIndex, regex, caseSensitive, wholeWord } =
                        editor.storage.zySearchAndReplace;

                    const replaced = applyReplace(tr, {
                        searchTerm,
                        replaceTerm,
                        results,
                        resultIndex,
                        regex,
                        caseSensitive,
                        wholeWord,
                    });

                    const nextIndex = resultIndex + 1;
                    if (results[nextIndex]) {
                        scrollActiveMatchIntoView(editor, results, nextIndex);
                    } else {
                        editor.storage.zySearchAndReplace.resultIndex = 0;
                        scrollActiveMatchIntoView(editor, results, 0);
                    }

                    return replaced;
                },
            replaceAll:
                () =>
                ({ editor, tr }) => {
                    const { searchTerm, replaceTerm, results, resultIndex, regex, caseSensitive, wholeWord } =
                        editor.storage.zySearchAndReplace;

                    return applyReplaceAll(tr, { searchTerm, replaceTerm, results, resultIndex, regex, caseSensitive, wholeWord });
                },
        };
    },

    addProseMirrorPlugins() {
        const editor = this.editor;

        const setLastSearchTerm = (t: string) => {
            editor.storage.zySearchAndReplace.lastSearchTerm = t;
        };
        const setLastCaseSensitive = (t: boolean) => {
            editor.storage.zySearchAndReplace.lastCaseSensitive = t;
        };
        const setLastWholeWord = (t: boolean) => {
            editor.storage.zySearchAndReplace.lastWholeWord = t;
        };
        const setLastRegex = (t: boolean) => {
            editor.storage.zySearchAndReplace.lastRegex = t;
        };

        return [
            new Plugin({
                key: zySearchAndReplacePluginKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, oldState, _oldEditorState, newEditorState) {
                        const meta = tr.getMeta(zySearchAndReplacePluginKey) as SearchPluginMeta | undefined;
                        const { doc } = newEditorState;
                        const { docChanged } = tr;
                        const {
                            searchTerm,
                            lastSearchTerm,
                            caseSensitive,
                            lastCaseSensitive,
                            wholeWord,
                            lastWholeWord,
                            resultIndex,
                            regex,
                            lastRegex,
                            results,
                        } = editor.storage.zySearchAndReplace;

                        if (meta?.type === 'updateResultIndex') {
                            return decorationsForResultIndex(doc, results, resultIndex);
                        }

                        if (
                            !docChanged &&
                            lastSearchTerm === searchTerm &&
                            lastCaseSensitive === caseSensitive &&
                            lastWholeWord === wholeWord &&
                            lastRegex === regex
                        ) {
                            return oldState;
                        }

                        setLastSearchTerm(searchTerm);
                        setLastCaseSensitive(caseSensitive);
                        setLastWholeWord(wholeWord);
                        setLastRegex(regex);

                        if (!searchTerm) {
                            editor.storage.zySearchAndReplace.results = [];
                            editor.storage.zySearchAndReplace.hasReplaceableMatch = false;
                            editor.storage.zySearchAndReplace.resultIndex = 0;
                            return DecorationSet.empty;
                        }

                        const {
                            decorationsToReturn,
                            results: newResults,
                            hasReplaceableMatch,
                        } = processSearches(doc, getRegex(searchTerm, !regex, caseSensitive, wholeWord), resultIndex);

                        editor.storage.zySearchAndReplace.results = newResults;
                        editor.storage.zySearchAndReplace.hasReplaceableMatch = hasReplaceableMatch;

                        if (resultIndex >= newResults.length) {
                            const clamped = newResults.length === 0 ? 0 : newResults.length - 1;
                            editor.storage.zySearchAndReplace.resultIndex = clamped;
                            return decorationsForResultIndex(doc, newResults, clamped);
                        }

                        return decorationsToReturn;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },
});

export default ZySearchAndReplace;
